import { throttle } from "lodash-es";

import { resetBoardScrollMargin } from "@/components/utils/boardRect";
import { pushZoomState } from "@/router/navigation";
import { useClientSettingsStore } from "@/store/clientSettings";
import { clientCoord } from "@/utils/dom/coordinates";
import { eventTarget } from "@/utils/dom/dom";
import { normalizeWheelEvent } from "@/utils/dom/wheelEvent";

import { processZoom } from "./zoom";

const scrollSensibility = 2;
const zoomSensibility = 0.014;
const debounceDelay = 150;

let moveTimeout: ReturnType<typeof setTimeout> | null = null;

export function initWheelHandler() {
  document.addEventListener("wheel", handleWheelEvent, { passive: false });
}

// A wheel event that occurs shortly after the last one is considered belonging to the same gesture.
// The same modifiers value will be used for it.
// This avoids the problem when the user releases the command key while mac's momentum scroll still produces wheel events,
// which causes panning of the board at the end of a zoom gesture.
const WHEEL_EVENT_TIMEOUT = 100;
let lastWheelEvent = 0;
let wheelModifiers = false;
function handleWheelEvent(e: WheelEvent) {
  if (isNoWheel(e)) {
    return;
  }
  e.preventDefault();

  const sameGesture = e.timeStamp - lastWheelEvent < WHEEL_EVENT_TIMEOUT;
  lastWheelEvent = e.timeStamp;
  const modifiers = sameGesture ? wheelModifiers : (wheelModifiers = e.ctrlKey || e.metaKey);
  const { x, y } = normalizeWheelEvent(e);
  updateInputDevice({ e, modifiers });

  // Handle movement or zoom
  const mouseMode = useClientSettingsStore().effectiveInputDevice === "mouse";
  const scroll = mouseMode === modifiers;
  if (scroll) {
    window.scrollBy(scrollSensibility * x, scrollSensibility * y);
  } else {
    if (Math.abs(y) > Math.abs(x)) {
      const zoomFactor = y > 0 ? 1 / (1 + y * zoomSensibility) : 1 - y * zoomSensibility;
      processZoom(clientCoord(e), zoomFactor, "relative");
    }
  }

  // Debounce the "stop moving" logic
  if (moveTimeout) {
    clearTimeout(moveTimeout);
  }

  // Set the timeout to detect when the user has stopped moving
  moveTimeout = setTimeout(() => {
    if (scroll) {
      resetBoardScrollMargin();
    } else {
      pushZoomState();
    }
  }, debounceDelay);
}

// found a .scrollable parent -> don't zoom but let default scrolling happen (of scrollable element)
// found a .no-wheel parent -> don't zoom and no default wheel action
// else not default wheel action, but zoom
function isNoWheel(e: MouseEvent): boolean {
  let el: HTMLElement | null = eventTarget(e);
  while (el !== null) {
    const hasVerticalScroll = el.scrollHeight !== el.offsetHeight;
    const hasHorizontalScroll = el.scrollWidth !== el.offsetWidth;
    if (el.classList.contains("scrollable") && (hasVerticalScroll || hasHorizontalScroll)) {
      return true;
    }
    if (el.classList.contains("no-wheel")) {
      e.preventDefault();
      return true;
    }
    el = el.parentElement;
  }
  e.preventDefault();
  return false;
}

/**
 * If the user has inputDevice set to "auto", this function checks which input device the user is *likely* using
 * based on scroll and key heuristics.
 *
 * Exported so canvases with their own wheel handling (e.g. the breakdown
 * graph, where the modal blocks the document-level handler) keep the
 * detection alive too.
 */
export const updateInputDevice = throttle(
  ({ e, modifiers }: { e: WheelEvent; modifiers: boolean }) => {
    const settings = useClientSettingsStore();
    if (settings.inputDevice !== "auto") {
      return;
    }

    // Stong heuristics: modifiers and horizontal scroll
    // modifiers: Assume someone doing ctrl+scroll is using a trackpad
    // (pinch-zoom is sent as a wheel event with ctrlKey=true,
    // on both windows and mac)
    //
    // deltaX: Assume someone doing horizontal scroll is using a trackpad
    // (you can do this with a mouse, but it's not common)
    if (modifiers || e.deltaX !== 0) {
      settings.setEffectiveInputDevice("trackpad");
      return;
    }

    // These are weaker heuristics, but should be consistent enough on major browsers, windows and macOS.
    if ("wheelDeltaY" in e) {
      const wheelDeltaY = Math.abs(e.wheelDeltaY as number);
      // Mouse+FF+Mac -> wheelDelta is a multiple of 48
      if (wheelDeltaY % 48 === 0) {
        settings.setEffectiveInputDevice("mouse");
        return;
      }

      if (wheelDeltaY % 120 === 0) {
        // Mouse + Win (FF or Chrome) -> wheelDelta tends to be a multiple of 120
        // This only works at 100% browser zoom, but let's try it and see
        settings.setEffectiveInputDevice("mouse");
        return;
      }

      // When scrolling on a trackpad, wheelDeltaY is usually 3 * deltaY
      if (Math.abs(wheelDeltaY / e.deltaY) === 3) {
        settings.setEffectiveInputDevice("trackpad");
        return;
      }
    }
  },
  500, // Only check every 500ms, which still makes it feel responsive when switching input devices
  { trailing: false, leading: true },
);
